import React, { FormEvent, useEffect, useState } from 'react'

import { RecordNote, RecordStatus, NoteType } from '../../models'
import { createRecordNote, deleteRecordNote, toastSuccess } from '../../api'
import { getAuthUser } from '../../helpers'
import { NoteDiv, TooltipWrapper } from '../common'
import AutoResizeTextarea from '../common/AutoResizeTextarea'
import CollapsibleNote from '../common/CollapsibleNote'

import defineAbilityFor from './defineAbility'

interface NoteProps {
    recordId: number
    recordStatus: number
    recordNotes: RecordNote[]
}

export const RecordNotes = (props: NoteProps) => {
    const { recordId, recordStatus, recordNotes } = props

    const [isAdding, setIsAdding] = useState(false)
    const [notes, setNotes] = useState(recordNotes)
    const [note, setNote] = useState('')
    const [noteTypes, setNoteTypes] = useState<string[]>([])
    const [selectedNoteType, setSelectedNoteType] = useState('')

    useEffect(() => {
        setNotes(recordNotes)
    }, [recordNotes])

    useEffect(() => {
        setNoteTypes([
            NoteType.GENERAL,
            NoteType.ACCOUNTING,
            NoteType.LEADERSHIP,
            NoteType.COLLECTION,
            NoteType.TIER2,
        ])
    }, [])

    const handleCancel = () => {
        setNote('')
        setSelectedNoteType('')
        setIsAdding(false)
    }

    const handleDelete = (recordNoteId: number) => {
        deleteRecordNote(recordNoteId)
            .then((json) => {
                setNotes(notes.filter((note) => note.id !== recordNoteId))

                toastSuccess('Note Deleted')
            })
            .catch(() => {})
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()

        const params = {
            recordId,
            note,
            noteType: selectedNoteType,
        }

        createRecordNote(params)
            .then((json) => {
                setNotes([...notes, new RecordNote(json.data)])
                setNote('')
                setSelectedNoteType('')
                setIsAdding(false)

                toastSuccess('Note Created')
            })
            .catch(() => {})
    }

    const ability = defineAbilityFor(getAuthUser())

    return (
        <>
            <div className="d-flex justify-content-between mb-3 mt-3">
                <h3>Notes</h3>
            </div>
            <form className="mb-3" onSubmit={(e) => handleSubmit(e)}>
                <TooltipWrapper
                    condition={
                        ![
                            RecordStatus.UNKNOWN_DEPOSIT,
                            RecordStatus.OVERPAYMENT,
                            RecordStatus.REFUND,
                        ].includes(recordStatus)
                    }
                    message="Notes can only be added when record is in Unknown Deposit, Overpayment, or Refund status"
                    element={
                        <AutoResizeTextarea
                            value={note}
                            onChange={setNote}
                            onFocus={() => setIsAdding(true)}
                            placeholder="Add a note"
                            disabled={
                                ![
                                    RecordStatus.UNKNOWN_DEPOSIT,
                                    RecordStatus.OVERPAYMENT,
                                    RecordStatus.REFUND,
                                ].includes(recordStatus)
                            }
                        />
                    }
                />
                {isAdding && (
                    <>
                        <label htmlFor="noteType" className="form-label">
                            Note Type
                        </label>
                        <select
                            className="form-select mb-4"
                            id="noteTypeInput"
                            value={selectedNoteType}
                            onChange={(e) => setSelectedNoteType(e.target.value)}
                            required
                        >
                            <option value="">Select Note Type</option>
                            {noteTypes.map((noteType) => (
                                <option key={noteType} value={noteType}>
                                    {noteType}
                                </option>
                            ))}
                        </select>
                        <button className="btn btn-primary me-2" type="submit">
                            Submit
                        </button>
                        <button className="btn btn-outline-secondary" onClick={handleCancel}>
                            Cancel
                        </button>
                    </>
                )}
            </form>
            {notes
                .sort((a, b) => {
                    if (b.created_at.isAfter(a.created_at)) {
                        return 1
                    }

                    if (a.created_at.isAfter(b.created_at)) {
                        return -1
                    }

                    return 0
                })
                .map((note) => (
                    <div className="card mb-3" key={`note_card_${note.id}`}>
                        <div className="card-header">
                            <strong className="me-4">{note.createdBy}</strong>
                            {note.created_at_local.format('MMM D, YYYY [at] h:mm A')}
                            <strong className="ms-5">{note.noteType}</strong>
                            <TooltipWrapper
                                condition={!ability.can('Delete', 'RecordNote')}
                                element={
                                    <button
                                        className="btn btn-sm btn-outline-danger float-end"
                                        disabled={!ability.can('Delete', 'RecordNote')}
                                        onClick={() => handleDelete(note.id)}
                                    >
                                        Delete
                                    </button>
                                }
                                message={'Executive permissions required'}
                            />
                        </div>
                        <NoteDiv className="card-body">
                            <CollapsibleNote text={note.note} maxLength={500} />
                        </NoteDiv>
                    </div>
                ))}
        </>
    )
}
